import { useMemo } from 'react';

interface UseDotsArguments {
  activeIndex: number;
  totalSlides: number;
  maxDotsToDisplay?: number;
}

/**
 * A hook that takes information about the active slide in a photo carousel
 * and returns an array of dot props (to be rendered by `PhotoCarousel.Dot`) that
 * visually represents the user's position inside of that set.
 *
 * @example
 * const dotsToRender = useDotsRange({
 *  activeIndex,
 *  totalSlides,
 *  maxDotsToDisplay,
 * });
 */
export const useDotsRange = (args: UseDotsArguments) => {
  const { activeIndex, totalSlides, maxDotsToDisplay = 5 } = args;

  /*
   * Memoize on the destructured primitives rather than on the `args`
   * object — callers invoke this with an inline object literal, so
   * depending on `[args]` would invalidate the memo on every render and
   * produce a fresh `Array<{...}>` reference.
   */
  return useMemo(() => {
    /* No need to show 1 or 0 dots. */
    if (totalSlides < 2 || maxDotsToDisplay < 2) return [];

    /**
     * Determine number of dots to display. Normally, it's `maxDotsToDisplay`
     * but if the actual number of slides is less, use that number instead.
     */
    const targetRange = Math.min(maxDotsToDisplay, totalSlides);

    /**
     * Calculate how many dots should be on each side of the active dot.
     * For even numbers, we'll have one more on one side.
     */
    const dotsOnLeft = Math.floor((targetRange - 1) / 2);
    const dotsOnRight = targetRange - 1 - dotsOnLeft;

    /**
     * Calculate the initial range, ensuring we don't exceed targetRange.
     */
    let min = Math.max(0, activeIndex - dotsOnLeft);
    let max = Math.min(activeIndex + dotsOnRight, totalSlides - 1);

    /**
     * Adjust the range if we're at the boundaries to ensure we show exactly
     * the number of dots equal to `targetRange`.
     */
    if (min === 0) {
      // We're at the left boundary, so extend to the right
      max = Math.min(targetRange - 1, totalSlides - 1);
    } else if (max === totalSlides - 1) {
      // We're at the right boundary, so extend to the left
      min = Math.max(0, totalSlides - targetRange);
    }

    /**
     * Return a dot for every slide but hide the ones outside of the group we
     * want to show. This way screen readers hear their correct position within
     * the entire dot list ("12 of 34") -- not just the visible list.
     *
     * Because we're using tab roles, the position (Ex: "2 of 10") is calculated by
     * the screen reader and not dictated by ARIA labels.
     *
     * An `isContinuation` dot will be rendered by `PhotoCarousel.Dot` as a smaller dot
     * to give visual affordance that there are more slides beyond it.
     */
    const items = Array.from({ length: totalSlides }, (_, i) => {
      const isActive = i === activeIndex;
      const isContinuation =
        !isActive && ((i === min && i > 0) || (i === max && i < totalSlides - 1));
      const isHidden = i < min || i > max;
      return { i, isActive, isContinuation, isHidden };
    });

    return items;
  }, [activeIndex, totalSlides, maxDotsToDisplay]);
};
